import type { ContentType, EstimationParams, ImageSizeTier, OutputSize } from '@tcc/shared';
import { TOTAL_INPUT_STEPS } from './state/state.js';

export const VOL_MAX = 1_000_000_000;

export const STEP_BADGE = ['1', '2', '3', '4', '5', '6'];
export const STEP_TITLE = [
	'Content type',
	'Type of interaction',
	'Inputs — volume & size',
	'Context layer',
	'Does this process involve decision making?',
	'Output size',
];
export const STEP_SIDEBAR = ['Content', 'Interaction', 'Inputs', 'Context', 'Decisions', 'Output size'];
export const STEP_DOTS = Array.from({ length: TOTAL_INPUT_STEPS }, (_, k) => k);

export const CONTENT_TYPES: { id: ContentType; label: string; desc: string; common?: boolean }[] = [
	{ id: 'text', label: 'Text & chat', desc: 'Q&A, drafting, chat assistants', common: true },
	{ id: 'images', label: 'Images', desc: 'Read screenshots, scans, photos' },
	{ id: 'voice', label: 'Voice & audio', desc: 'Transcribe or analyse calls' },
	{ id: 'video', label: 'Video', desc: 'Analyse recordings and footage' },
];

export const IMAGE_SIZES: { id: ImageSizeTier; label: string }[] = [
	{ id: 'small', label: 'Icon (≤384px)' },
	{ id: 'medium', label: 'Small (≤512px)' },
	{ id: 'large', label: 'Standard (≤1024px)' },
	{ id: 'hd', label: 'HD (≤1920px)' },
	{ id: 'ultra', label: '4K / very large' },
];

export const OUTPUT_SIZES: { id: OutputSize; label: string; desc: string }[] = [
	{ id: 'paragraph', label: 'Paragraph', desc: 'A short reply or summary (~130 tokens).' },
	{ id: 'page', label: 'Page', desc: 'About a page of text (~750 tokens).' },
	// A page is ~750 tokens; a full document is ~3,000 tokens (4 pages).
	{ id: 'document', label: 'Full document', desc: 'A long, multi-section output — about 4 pages (~3,000 tokens).' },
];

/** Units offered for "additional context" sizing (paragraph / page / document). */
export const CONTEXT_UNITS = OUTPUT_SIZES;

/**
 * Options shown for the AI's response size (final step). Paragraph is omitted
 * here — the smallest response we offer is a page.
 */
export const RESPONSE_SIZES = OUTPUT_SIZES.filter((o) => o.id !== 'paragraph');

export const PRICING_TIERS: { id: 'standard' | 'priority' | 'flexBatch'; label: string }[] = [
	{ id: 'standard', label: 'Standard' },
	{ id: 'priority', label: 'Priority' },
	{ id: 'flexBatch', label: 'Flex / Batch' },
];

export const PT_ROWS: { key: keyof EstimationParams['ptSizing']; label: string }[] = [
	{ key: 'gemini25Flash', label: 'Gemini 2.5 Flash' },
	{ key: 'gemini25FlashLite', label: 'Gemini 2.5 Flash-Lite' },
	{ key: 'gemini25Pro', label: 'Gemini 2.5 Pro' },
	{ key: 'gemini31FlashLite', label: 'Gemini 3.1 Flash-Lite' },
	{ key: 'gemini31Pro', label: 'Gemini 3.1 Pro' },
];

export const RUNTIME_ROWS: { key: keyof EstimationParams['runtimeAssumptions']; label: string }[] = [
	{ key: 'simpleChatbot', label: 'Simple chatbot' },
	{ key: 'standardAgentic', label: 'Standard agentic' },
	{ key: 'complexMultiAgent', label: 'Complex multi-agent' },
	{ key: 'heavyToolUse', label: 'Heavy tool use' },
];

export const SIZING_ROWS: { key: keyof EstimationParams['sizingRanges']; label: string }[] = [
	{ key: 'textPages', label: 'Text (pages)' },
	{ key: 'voiceMinutes', label: 'Voice (minutes)' },
	{ key: 'imageCount', label: 'Images (count)' },
	{ key: 'videoMinutes', label: 'Video (minutes)' },
];
